// Reminders (Ryan, Oct 26 2026): "If someone asks AI for a reminder at a date and time, show it as a
// date and time and as a banner, and track it with their other stuff."
//   • ReminderAskCard — Ask AI's own answer to "remind me …": what and when it understood, editable,
//     Save. Read by the app (server/reminderParse.js), not the AI.
//   • ReminderBanner — app-wide: a reminder that has come due sits across the top until Done or
//     Snooze (the bell rings once from the server too).
//   • MyReminders — the home page's ⏰ My Reminders: what's coming, add one by hand, Done / ✕.
// Everyone's reminders are their own.
import React, { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../services/apiClient';

const TZ = 'America/Los_Angeles';
const changed = () => window.dispatchEvent(new Event('titan:reminders-changed'));
const signedIn = () => { try { return !!JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return false; } };
// "Tue, Oct 27 · 7:00 AM" in Reno time.
export const whenLabel = iso => new Date(iso).toLocaleString('en-US', { timeZone: TZ, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
// ISO → the date-time box's 'YYYY-MM-DDTHH:MM', in Reno time.
export function localInput(iso) {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(new Date(iso)).map(x => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
const tomorrow8 = () => { const d = new Date(Date.now() + 864e5); return localInput(d.toISOString()).slice(0, 10) + 'T08:00'; };
const inp = { border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff', color: '#111' };
const btn = (bg, fg, bd) => ({ padding: '6px 12px', borderRadius: 999, background: bg, color: fg, border: `1px solid ${bd || bg}`, fontWeight: 800, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' });

// Is this a "remind me …" message?
export const isReminderAsk = text => /^\s*(?:(?:hey|ok|okay|please|pls|can you|could you|would you|titan)[\s,]+)*(?:remind\s+me\b|set\s+(?:me\s+)?(?:up\s+)?(?:a\s+)?reminder\b|reminder\s*(?::|to|for|at|on|tomorrow|today|tonight)\b)/i.test(String(text || ''));

// ── Ask AI's card ────────────────────────────────────────────────────────────────────────────────
export function ReminderAskCard({ asked, parsed, onAskAI }) {
  const [what, setWhat] = useState(parsed.what || '');
  const [when, setWhen] = useState(parsed.remind_at ? localInput(parsed.remind_at) : '');
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function save() {
    setBusy(true); setErr('');
    try { const r = await apiFetch('/reminders', { method: 'POST', body: JSON.stringify({ what, remind_local: when, source: 'ask_ai', asked_text: asked }) }); setSaved(r); changed(); }
    catch (e) { setErr(e?.error || 'Could not save it.'); }
    setBusy(false);
  }
  if (saved) return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#15803d', marginBottom: 5 }}>⏰ Reminder saved</div>
      <div style={{ fontWeight: 700 }}>{saved.what}</div>
      <div style={{ fontSize: 15, fontWeight: 800, color: '#1e3a5f', marginTop: 2 }}>{whenLabel(saved.remind_at)}</div>
      <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 6 }}>It pops up as a banner at that time (and in your bell). It's on your home page under ⏰ My Reminders.</div>
    </div>
  );
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#b45309', marginBottom: 6 }}>⏰ Reminder{parsed.needs_when ? ' — when?' : ''}</div>
      <input value={what} onChange={e => setWhat(e.target.value)} placeholder="Remind me to…" style={{ ...inp, width: '100%', fontWeight: 700 }} />
      <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} style={{ ...inp, width: '100%', marginTop: 6, fontWeight: 800, color: '#1e3a5f' }} />
      {when && <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{whenLabel(new Date(when + ':00').toISOString())}{parsed.guessed_time && when === (parsed.remind_at && localInput(parsed.remind_at)) ? ' — no time was said, so 8:00 AM' : ''}</div>}
      {parsed.needs_when && !when && <div style={{ fontSize: 12.5, color: '#b45309', marginTop: 4 }}>I didn't catch when — pick a date and time.</div>}
      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', marginTop: 4 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 9 }}>
        <button type="button" onClick={save} disabled={busy || !what.trim() || !when} style={{ ...btn('#15803d', '#fff'), opacity: busy || !what.trim() || !when ? 0.5 : 1 }}>{busy ? 'Saving…' : '✓ Save reminder'}</button>
        {onAskAI && <button type="button" onClick={onAskAI} style={btn('#fff', '#6b7280', '#d1d5db')}>Not a reminder — ask the AI</button>}
      </div>
    </div>
  );
}

// ── The banner ───────────────────────────────────────────────────────────────────────────────────
export function ReminderBanner() {
  const [due, setDue] = useState([]);
  const [more, setMore] = useState(false);
  const load = useCallback(() => {
    if (!signedIn()) { setDue([]); return; }
    apiFetch('/my-reminders').then(r => setDue((r && r.due) || [])).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('titan:reminders-changed', load);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('titan:reminders-changed', load); };
  }, [load]);
  async function act(r, body) {
    setDue(d => d.filter(x => x.id !== r.id));
    try { await apiFetch(`/my-reminders/${r.id}`, { method: 'PATCH', body: JSON.stringify(body) }); } catch { /* reload puts it back */ }
    changed();
  }
  if (!due.length) return null;
  const shown = more ? due : due.slice(0, 2);
  return (
    <div style={{ position: 'fixed', top: 'max(env(safe-area-inset-top), 8px)', left: '50%', transform: 'translateX(-50%)', zIndex: 4800, width: 'min(560px, calc(100vw - 16px))', display: 'flex', flexDirection: 'column', gap: 6 }}>
      {shown.map(r => (
        <div key={r.id} role="alert" style={{ background: '#fffbeb', border: '2px solid #f59e0b', borderRadius: 12, padding: '10px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 18 }}>⏰</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#111', wordBreak: 'break-word' }}>{r.what}</div>
              <div style={{ fontSize: 12, color: '#92400e', fontWeight: 700 }}>{whenLabel(r.remind_at)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
            <button onClick={() => act(r, { action: 'done' })} style={btn('#15803d', '#fff')}>✓ Done</button>
            <button onClick={() => act(r, { action: 'snooze', minutes: 15 })} style={btn('#fff', '#92400e', '#fcd34d')}>15 min</button>
            <button onClick={() => act(r, { action: 'snooze', minutes: 60 })} style={btn('#fff', '#92400e', '#fcd34d')}>1 hour</button>
            <button onClick={() => act(r, { action: 'snooze', until: 'tomorrow' })} style={btn('#fff', '#92400e', '#fcd34d')}>Tomorrow 8 AM</button>
          </div>
        </div>
      ))}
      {due.length > 2 && <button onClick={() => setMore(m => !m)} style={{ ...btn('#fff', '#92400e', '#fcd34d'), alignSelf: 'center' }}>{more ? 'Show fewer' : `+${due.length - 2} more due`}</button>}
    </div>
  );
}

// ── The home page list ───────────────────────────────────────────────────────────────────────────
export function MyReminders() {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [what, setWhat] = useState('');
  const [when, setWhen] = useState(tomorrow8());
  const [edit, setEdit] = useState(null);        // { id, what, when }
  const [showDone, setShowDone] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(() => { apiFetch('/my-reminders').then(setData).catch(() => setData({ open: [], done: [], due: [] })); }, []);
  useEffect(() => { load(); window.addEventListener('titan:reminders-changed', load); return () => window.removeEventListener('titan:reminders-changed', load); }, [load]);
  async function call(path, method, body) {
    setErr('');
    try { await apiFetch(path, { method, ...(body ? { body: JSON.stringify(body) } : {}) }); changed(); return true; }
    catch (e) { setErr(e?.error || 'That did not save.'); return false; }
  }
  if (!data) return null;
  const open = data.open || [], done = data.done || [];
  // Only on the page when something is coming up (Ryan, Oct 26 2026) — new ones come from Ask AI
  // ("remind me …"), and the card shows up as soon as one is saved.
  if (!open.length && !adding && !edit) return null;
  return (
    <div style={{ background: '#fff', border: '1.5px solid #fde68a', borderLeft: '5px solid #f59e0b', borderRadius: 14, padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: '#b45309', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>⏰ My Reminders{open.length ? ` · ${open.length}` : ''}</span>
        <button onClick={() => { setAdding(a => !a); setWhat(''); setWhen(tomorrow8()); }} style={btn('#fffbeb', '#b45309', '#fcd34d')}>{adding ? 'Cancel' : '＋ Add'}</button>
      </div>
      {adding && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input autoFocus value={what} onChange={e => setWhat(e.target.value)} placeholder="Remind me to…" style={inp} />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <input type="datetime-local" value={when} onChange={e => setWhen(e.target.value)} style={{ ...inp, flex: 1, minWidth: 190 }} />
            <button disabled={!what.trim() || !when} onClick={async () => { if (await call('/reminders', 'POST', { what, remind_local: when, source: 'home' })) setAdding(false); }}
              style={{ ...btn('#15803d', '#fff'), opacity: !what.trim() || !when ? 0.5 : 1 }}>Save</button>
          </div>
        </div>
      )}
      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', marginTop: 6 }}>{err}</div>}
      {!open.length && !adding && <div style={{ fontSize: 13.5, color: '#9ca3af', marginTop: 6 }}>Nothing coming up. Tell Ask AI "remind me tomorrow at 7 to…", or tap ＋ Add.</div>}
      {open.map(r => (edit && edit.id === r.id ? (
        <div key={r.id} style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <input value={edit.what} onChange={e => setEdit({ ...edit, what: e.target.value })} style={inp} />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <input type="datetime-local" value={edit.when} onChange={e => setEdit({ ...edit, when: e.target.value })} style={{ ...inp, flex: 1, minWidth: 190 }} />
            <button onClick={async () => { if (await call(`/my-reminders/${r.id}`, 'PATCH', { what: edit.what, remind_local: edit.when })) setEdit(null); }} style={btn('#15803d', '#fff')}>Save</button>
            <button onClick={() => setEdit(null)} style={btn('#fff', '#6b7280', '#d1d5db')}>Cancel</button>
          </div>
        </div>
      ) : (
        <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderTop: '1px solid #f3f4f6', marginTop: 6 }}>
          <button onClick={() => call(`/my-reminders/${r.id}`, 'PATCH', { action: 'done' })} title="Done" aria-label="Done"
            style={{ width: 26, height: 26, flexShrink: 0, borderRadius: 999, border: '2px solid #86efac', background: '#fff', color: '#15803d', fontWeight: 900, cursor: 'pointer', fontSize: 13 }}>✓</button>
          <div role="button" tabIndex={0} onClick={() => setEdit({ id: r.id, what: r.what, when: localInput(r.remind_at) })} style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}>
            <div style={{ fontSize: 14.5, fontWeight: 700, color: '#111', wordBreak: 'break-word' }}>{r.what}</div>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: r.due ? '#b91c1c' : '#1e3a5f' }}>{r.due ? '⏰ Due · ' : ''}{whenLabel(r.remind_at)}{r.source === 'ask_ai' ? ' · from Ask AI' : ''}</div>
          </div>
          <button onClick={() => { if (window.confirm('Delete this reminder?')) call(`/my-reminders/${r.id}`, 'DELETE'); }} title="Delete" aria-label="Delete"
            style={{ border: 'none', background: 'none', color: '#9ca3af', fontSize: 18, cursor: 'pointer', padding: '0 4px' }}>×</button>
        </div>
      )))}
      {done.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <button onClick={() => setShowDone(v => !v)} style={{ border: 'none', background: 'none', padding: 0, color: '#6b7280', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{showDone ? '▾' : '▸'} Done this week · {done.length}</button>
          {showDone && done.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13, color: '#9ca3af' }}>
              <span style={{ flex: 1, minWidth: 0, textDecoration: 'line-through' }}>{r.what}</span>
              <button onClick={() => call(`/my-reminders/${r.id}`, 'PATCH', { action: 'undo' })} style={{ border: 'none', background: 'none', color: '#6b7280', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Undo</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
